import { Directive, HostListener, Input, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';

import {
  sanitizeDecimal,
  sanitizeDigits,
  sanitizePhone,
} from '../utils/input-validation.util';

/**
 * Digits only (quantities, OTP).
 * Use: <input type="number" appDigitsOnly [appMaxDigits]="6" />
 */
@Directive({
  selector: 'input[appDigitsOnly]',
  standalone: true,
})
export class DigitsOnlyDirective {
  @Input() appMaxDigits?: number;

  constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {}

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (isModifierOrNavKey(event)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    applySanitizedValue(
      event.target as HTMLInputElement,
      sanitizeDigits((event.target as HTMLInputElement).value, this.appMaxDigits),
      this.ngControl,
    );
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    applySanitizedValue(
      event.target as HTMLInputElement,
      sanitizeDigits(pasted, this.appMaxDigits),
      this.ngControl,
    );
  }
}

/**
 * Decimal numbers (prices, costs).
 * Use: <input type="number" appDecimalOnly [appMaxDecimals]="2" />
 */
@Directive({
  selector: 'input[appDecimalOnly]',
  standalone: true,
})
export class DecimalOnlyDirective {
  @Input() appMaxDecimals = 2;
  @Input() appAllowNegative = false;

  constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {}

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (isModifierOrNavKey(event)) {
      return;
    }
    if (/^\d$/.test(event.key) || event.key === '.') {
      return;
    }
    if (this.appAllowNegative && event.key === '-') {
      return;
    }
    event.preventDefault();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    applySanitizedValue(
      el,
      sanitizeDecimal(el.value, {
        allowNegative: this.appAllowNegative,
        maxDecimals: this.appMaxDecimals,
      }),
      this.ngControl,
    );
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    applySanitizedValue(
      event.target as HTMLInputElement,
      sanitizeDecimal(pasted, {
        allowNegative: this.appAllowNegative,
        maxDecimals: this.appMaxDecimals,
      }),
      this.ngControl,
    );
  }
}

/**
 * Phone characters only.
 * Use: <input type="tel" appPhoneOnly />
 */
@Directive({
  selector: 'input[appPhoneOnly]',
  standalone: true,
})
export class PhoneOnlyDirective {
  @Input() appMaxPhoneLength = 20;

  constructor(@Optional() @Self() private readonly ngControl: NgControl | null) {}

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (isModifierOrNavKey(event)) {
      return;
    }
    if (/^[\d+\-\s()]$/.test(event.key)) {
      return;
    }
    event.preventDefault();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    applySanitizedValue(
      el,
      sanitizePhone(el.value, this.appMaxPhoneLength),
      this.ngControl,
    );
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    applySanitizedValue(
      event.target as HTMLInputElement,
      sanitizePhone(pasted, this.appMaxPhoneLength),
      this.ngControl,
    );
  }
}

function applySanitizedValue(
  el: HTMLInputElement,
  next: string,
  ngControl: NgControl | null,
): void {
  if (el.value !== next) {
    el.value = next;
  }

  const control = ngControl?.control;
  if (control && String(control.value ?? '') !== next) {
    control.setValue(next);
  }
}

function isModifierOrNavKey(event: KeyboardEvent): boolean {
  return (
    event.ctrlKey ||
    event.metaKey ||
    event.altKey ||
    [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ].includes(event.key)
  );
}
